import { useCallback, useState } from 'react';
import uuid from 'react-uuid';
import useAsync from 'src/hooks/useAsync';
import {
  IVarianceDetails,
  IVarianceGLCode,
  IVarianceGLPeriod,
  IVarianceLineItem,
} from 'src/types/VarianceDetails';
import { round } from 'src/utils';
import axiosInstance from 'src/utils/axios';

const useVarianceLineItemApi = (varianceId?: string) => {
  const [varianceLineItems, setVarianceLineItems] = useState<IVarianceLineItem[]>([]);
  const [varianceGLPeriods, setVarianceGLPeriods] = useState<IVarianceGLPeriod[]>([]);
  const [varianceGLCodes, setVarianceGLCodes] = useState<IVarianceGLCode[]>([]);
  const call = useCallback(async () => {
    try {
      const res = await axiosInstance.get<IVarianceDetails>(`Variance/${varianceId}`);
      const { periodSummaries = [] } = res.data?.lineItemVariances[0];

      const acctPeriodVariances: IVarianceGLPeriod[] = [...res.data.acctPeriodVariances]
        .sort((a, b) => {
          const startDateA = new Date(a.acctPeriod.split('-')[0]);
          const startDateB = new Date(b.acctPeriod.split('-')[0]);
          return startDateA.getTime() - startDateB.getTime();
        })
        .map((item) => {
          const glCodeVariances = item.glCodeVariances.map((glCode) => {
            const itemVariances = glCode.itemVariances.map((lineItem) => ({
              ...lineItem,
              docQty: round(lineItem.docQty, 2),
              poQty: round(lineItem.poQty, 2),
              rowId: uuid(),
              qtyVariance: round(lineItem.docQty - lineItem.poQty, 2),
              subTotalVariance: round(lineItem.docSubTotal - lineItem.poSubTotal, 2),
              hasQtyVariance: lineItem.docQty !== lineItem.poQty,
              hasSubTotalVariance: lineItem.docSubTotal !== lineItem.poSubTotal,
            }));

            return {
              ...glCode,
              rowId: uuid(),
              expanded: false,
              docQty: round(glCode.docQty, 2),
              poQty: round(glCode.poQty, 2),
              qtyVariance: round(glCode.docQty - glCode.poQty, 2),
              subTotalVariance: round(glCode.docSubTotal - glCode.poSubTotal, 2),
              hasQtyVariance:
                itemVariances.some((i) => !!i.hasQtyVariance) || glCode.docQty !== glCode.poQty,
              hasSubTotalVariance:
                itemVariances.some((i) => !!i.hasSubTotalVariance) ||
                glCode.docSubTotal !== glCode.poSubTotal,
              itemVariances,
            };
          });

          const invoices =
            periodSummaries.find((s) => s.acctPeriod === item.acctPeriod)?.invoices ?? [];

          return {
            ...item,
            glCodeVariances,
            rowId: uuid(),
            expanded: false,
            docQty: round(item.docQty, 2),
            poQty: round(item.poQty, 2),
            qtyVariance: round(item.docQty - item.poQty, 2),
            subTotalVariance: round(item.docSubTotal - item.poSubTotal, 2),
            hasQtyVariance:
              glCodeVariances.some((i) => !!i.hasQtyVariance) || item.docQty !== item.poQty,
            hasSubTotalVariance:
              glCodeVariances.some((i) => !!i.hasSubTotalVariance) ||
              item.docSubTotal !== item.poSubTotal,
            warnings: item.glCodeVariances.every((glCode) => glCode.poQty === 0)
              ? ['Period not on PO']
              : invoices.every((invoice) => invoice.quantity === 0)
              ? ['Period on PO but not on docs']
              : [],
          };
        });

      const glVariances: IVarianceGLCode[] = [...res.data.glVariances].map((item) => {
        const taxVariance = round(item.invoicedTaxes - item.poTaxes, 2);
        const shippingVariance = round(item.invoicedShipping - item.poShipping, 2);
        const discountVariance = round(item.poDiscount - item.invoicedDiscount, 2);
        return {
          ...item,
          glCdDescription: `${item.glCd} - ${item.description}`,
          invoicedDiscount: round(item.invoicedDiscount, 2),
          invoicedShipping: round(item.invoicedShipping, 2),
          invoicedTaxes: round(item.invoicedTaxes, 2),
          poDiscount: round(item.poDiscount, 2),
          poShipping: round(item.poShipping, 2),
          poTaxes: round(item.poTaxes, 2),
          taxVariance,
          shippingVariance,
          discountVariance,
          totalVariance: round(taxVariance + discountVariance + shippingVariance, 2),
          rowId: uuid(),
        };
      });

      const lineItemVariances: IVarianceLineItem[] = [...res?.data?.lineItemVariances]?.map(
        (item) => {
          const poSubTotal = round(item.poQty * item.poPrice, 2);
          const periodSummaries = item.periodSummaries
            ?.sort((a, b) => {
              const startDateA = new Date(a.acctPeriod.split('-')[0]);
              const startDateB = new Date(b.acctPeriod.split('-')[0]);
              return startDateA.getTime() - startDateB.getTime();
            })
            ?.map((summary) => {
              const invoiceLineItem = summary.invoices.map((lineItem) => ({
                ...lineItem,
                rowId: uuid(),
                invoiceGlCdDescription: `${lineItem.glCode} - ${lineItem.glCdDescription}`,
                hasGlCodeVariance: item.glCd !== lineItem.glCode,
                hasUOMVariance: item.poUom !== lineItem.uom,
              }));

              return {
                ...summary,
                rowId: uuid(),
                expanded: false,
                invoices: invoiceLineItem,
                periodQtyVariance: round(summary.periodDocQty - summary.periodPoQty, 2),
                periodSubtotalVariance: round(
                  summary.periodDocSubTotal - summary.periodPoSubTotal,
                  2
                ),
                hasQtyVariance: summary.periodPoQty !== summary.periodDocQty,
                hasSubTotalVariance: summary.periodPoSubTotal !== summary.periodDocSubTotal,
                hasGlCodeVariance: invoiceLineItem.some((i) => !!i.hasGlCodeVariance),
                hasUOMVariance: invoiceLineItem.some((i) => !!i.hasUOMVariance),
                warnings:
                  summary.periodPoQty === 0
                    ? ['Period not on PO']
                    : summary.periodDocQty === 0
                    ? ['Period on PO but not on docs']
                    : [],
              };
            });

          return {
            ...item,
            rowId: uuid(),
            expanded: false,
            poSubTotal,
            poQtyVariance: round(item.invoicedQuantity - item.poQty, 2),
            poSubTotalVariance: round(item.invoicedSubTotal - poSubTotal, 2),
            periodSummaries,
            poGlCdDescription: `${item.glCd} - ${item.glCdDescription}`,
            hasGlCodeVariance: periodSummaries.some((i) => !!i.hasGlCodeVariance),
            hasUOMVariance: periodSummaries.some((i) => !!i.hasUOMVariance),
            hasQtyVariance:
              periodSummaries.some((i) => !!i.hasQtyVariance) ||
              item.invoicedQuantity !== item.poQty,
            hasSubTotalVariance:
              periodSummaries.some((i) => !!i.hasSubTotalVariance) ||
              item.invoicedSubTotal !== poSubTotal,
          };
        }
      );

      setVarianceGLPeriods(acctPeriodVariances);
      setVarianceGLCodes(glVariances);
      setVarianceLineItems(lineItemVariances);
      return {
        ...res.data,
        lineItemVariances,
        glVariances,
        acctPeriodVariances,
      } as IVarianceDetails;
    } catch (error) {
      throw new Error(error);
    }
  }, [varianceId]);

  const { value, setValue, execute, isLoading } = useAsync(
    call,
    true,
    undefined,
    {} as IVarianceDetails
  );

  return {
    variances: value,
    setVariances: setValue,
    varianceLineItems,
    setVarianceLineItems,
    varianceGLCodes,
    setVarianceGLCodes,
    varianceGLPeriods,
    setVarianceGLPeriods,
    getVariances: execute,
    isVarianceLoading: isLoading,
  };
};

export default useVarianceLineItemApi;
